import { Column, Row } from 'Components/Grid';
import { Button, Dialog, RadioGroup } from 'Components/ui';
import { DropdownMenu } from 'Components/common/DropdownMenu';
import { ChevronDownIcon } from 'Components/common/Icons';
import styled from 'styled-components';
import LogoOutlineIcon from '../../../assets/logo_outline.png';
import HourGlassIcon from '../../../assets/hourglass.png';
import { useMutation, useQuery } from 'react-query';
import { useEffect, useState } from 'react';
import { createCheckoutSession, getPricesByProductCheckout, getProductsCheckout } from 'services/checkout-service';
import { useParams } from 'react-router-dom';
import { formatMoney } from 'utils/number_utils';
import { getPresetPlan } from 'services/company-service';
import { useTranslation } from 'react-i18next';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  height: 100%;
  padding-inline: 24px;
  font-family: var(--font-body);
  background-color: var(--vo-white);
`

const AccordionBox = styled.div`
  background: white;
  box-shadow: 0px 6px 20px var(--vo-escuro-8);
  border-radius: 8px;
  padding: 8px 16px 16px;
  &::before { display: none; }
`

const AccordionBoxFlatTop = styled(AccordionBox)`
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  padding: 8px 0px 16px;
  margin-top: 0;
`

const AccordionHeader = styled.button`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  text-align: left;
`

const AccordionHeaderBordered = styled(AccordionHeader)`
  border-bottom: 1px solid var(--vo-cloud);
  max-height: 48px;
  min-height: 48px;
  align-items: center;
  padding: 0 16px;
`

const ChevronBox = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;
  transform: rotate(${({ $expanded }) => $expanded ? '180deg' : '0deg'});
`

const AccordionContent = styled.div`
  padding-top: 16px;
`

const AccordionContentPadded = styled(AccordionContent)`
  padding-left: 16px;
  padding-right: 16px;
`


const LabelPrimary = styled.span`
  color: var(--vo-ink);
  font-weight: 700;
  font-size: 14px;
`


const LabelDisabled = styled.span`
  color: var(--vo-steel);
  font-weight: 400;
  font-size: 14px;
`

const LabelDisabledBold = styled.span`
  color: var(--vo-steel);
  font-weight: 700;
  font-size: 14px;
`

const LabelAccent = styled.span`
  color: var(--color-brand-live);
  font-weight: 700;
  font-size: 14px;
`

const PlanCard = styled.div`
  box-shadow: 0px 6px 20px var(--vo-cloud);
  border-radius: 8px;
  background-color: var(--vo-white);
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 3rem;
  padding: 16px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;

const LabelPrimaryLarge = styled.span`
  color: var(--vo-ink);
  font-weight: 700;
  font-size: 19px;
`

export const OnboardChoosePlan = () => {
  const { t } = useTranslation(['company']);
  const { slug } = useParams();
  const [expandSelectPeriod, setExpandSelectPeriod] = useState(true);
  const [expandDetailPlan, setExpandDetailPlan] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPeriodPlan, setSelectedPeriodPlan] = useState(null);
  const { data: presetPlan } = useQuery(['getPresetPlan', slug], async () => {
    if (!slug) return null;
    const result = await getPresetPlan(slug);
    return result?.data ? result?.data : null;
  });

  const {
    data: products,
    isLoading: productLoading,
    error: productError,
  } = useQuery(['getProducts'], async () => {
    const result = await getProductsCheckout();
    return result?.data.data.length > 0 ? result?.data.data : [];
  });

  const {
    data: prices,
    isLoading: priceLoading,
    error: priceError,
  } = useQuery(['getPricesByProduct', selectedPlan], async () => {
    if (!selectedPlan) return [];
    const result = await getPricesByProductCheckout(selectedPlan.id);
    return result?.data.data.length > 0 ? result?.data.data : [];
  });

  const mutation = useMutation(
    priceItem => {
      return createCheckoutSession(priceItem.id, slug);
    },
    {
      onError: error => {
        // An error happened!
        Dialog.alert({
          title: t('company:onboard.choosePlan.errorTitle'),
          message: error.response.data.message ?? error.message,
          icon: 'error',
        });
      },
      onSuccess: data => {
        if (data.status === 200) {
          window.location.href = data.data.url;
        }
      },
    },
  );

  useEffect(() => {
    if (!selectedPlan && products?.length > 0 && !presetPlan) {
      setSelectedPlan(products[0]);
    } else if (!selectedPlan && products?.length > 0 && presetPlan) {
      setSelectedPlan(products.find(product => product.metadata.vo_key === presetPlan.plan));
    }
  }, [products, selectedPlan, presetPlan]);

  useEffect(() => {
    if (selectedPlan && !selectedPeriodPlan && prices?.length > 0) {
      setSelectedPeriodPlan(prices[0]);
    }
  }, [prices, selectedPlan, selectedPeriodPlan]);

  const goToLoginPage = () => {
    const urlSplit = window.location.origin.split('/');
    const baseUrl = urlSplit[2].substring(3);
    window.location.href = `https://${slug}.${baseUrl}/login`;
  };

  const priceOptions = prices.map(p => ({
    value: p.id,
    label: p.recurring.interval === 'month' ? t('company:onboard.choosePlan.perMonth') : t('company:onboard.choosePlan.perYear'),
    description: `${formatMoney(p.unit_amount * 0.01)} ${p.recurring.interval === 'month' ? t('company:onboard.choosePlan.perMonthShort') : t('company:onboard.choosePlan.perYearShort')}`,
  }))

  return (
    <PageContainer className="container">
      <Row alignItems="start">
        <Column sm={8}>
          <div className="mb-4 mt-5">
            <img
              alt="Logo com borda azulada e plano de fundo vazado"
              src={LogoOutlineIcon}
              style={{
                maxHeight: '240px',
                marginInline: '0',
                display: 'flex',
              }}
            />
          </div>
          <AccordionBox>
            <AccordionHeader onClick={() => setExpandSelectPeriod(!expandSelectPeriod)}>
              <Row>
                <Column sm={'auto'}>
                  <img
                    alt="Ampulheta"
                    src={HourGlassIcon}
                    style={{
                      margin: '0',
                      display: 'flex',
                    }}
                  />
                </Column>
                <Column sm={10} className="pl-0">
                  {t('company:onboard.choosePlan.selectPeriod')}
                </Column>
                <Column sm={12}>
                  <LabelDisabled>{t('company:onboard.choosePlan.billingFrequency')}</LabelDisabled>
                </Column>
              </Row>
              <ChevronBox $expanded={expandSelectPeriod}>
                <ChevronDownIcon size={24} fill={'var(--vo-slate)'} />
              </ChevronBox>
            </AccordionHeader>
            {expandSelectPeriod && (
              <AccordionContent>
                {!priceLoading && !priceError && prices.length > 0 && (
                  <RadioGroup
                    options={priceOptions}
                    value={selectedPeriodPlan?.id || ''}
                    onChange={(value) => {
                      const price = prices.find(p => p.id === value)
                      if (price) setSelectedPeriodPlan(price)
                    }}
                  />
                )}
              </AccordionContent>
            )}
          </AccordionBox>
        </Column>
        <Column sm={4}>
          <PlanCard>
            <Row>
              <Column sm={7}>
                <LabelPrimary>
                  {selectedPlan ? selectedPlan?.description : t('company:onboard.choosePlan.selectPlan')}
                </LabelPrimary>
              </Column>
              <Column flex justifyContent="end">
                {!productLoading && !productError && (
                  <DropdownMenu
                    trigger={<Button variant="ghost" style={{ color: 'var(--color-brand-live)' }}>{t('company:onboard.choosePlan.changePlan')}</Button>}
                    items={products?.map(product => ({
                      label: product.name,
                      onClick: () => {
                        setSelectedPlan(product);
                        setSelectedPeriodPlan(null);
                      },
                    })) || []}
                  />
                )}
              </Column>
            </Row>
            <Row>
              <Column>
                <LabelDisabled>
                  {selectedPlan?.description || ''}
                </LabelDisabled>
              </Column>
            </Row>
          </PlanCard>

          <AccordionBoxFlatTop>
            <AccordionHeaderBordered onClick={() => setExpandDetailPlan(!expandDetailPlan)}>
              <LabelAccent>{t('company:onboard.choosePlan.planDetails')}</LabelAccent>
              <ChevronBox $expanded={expandDetailPlan}>
                <ChevronDownIcon size={24} fill={'var(--vo-slate)'} />
              </ChevronBox>
            </AccordionHeaderBordered>
            {expandDetailPlan && (
              <AccordionContentPadded>
                <Row style={{ maxHeight: '18px' }}>
                  <Column>
                    <LabelDisabledBold>{t('company:onboard.choosePlan.subtotal')}</LabelDisabledBold>
                  </Column>
                  <Column flex justifyContent="end">
                    <LabelPrimary>{selectedPeriodPlan ? formatMoney(selectedPeriodPlan.unit_amount * 0.01) : 'R$0,00'}</LabelPrimary>
                  </Column>
                </Row>
                <Row style={{ maxHeight: '18px' }}>
                  <Column>
                    <LabelDisabledBold>{t('company:onboard.choosePlan.discount')}</LabelDisabledBold>
                  </Column>
                  <Column flex justifyContent="end">
                    <LabelPrimary>R$0,00</LabelPrimary>
                  </Column>
                </Row>
                <Row style={{ maxHeight: '36px' }} className="mt-3">
                  <Column>
                    <LabelDisabledBold>{t('company:onboard.choosePlan.total')}</LabelDisabledBold>
                  </Column>
                  <Column flex justifyContent="end">
                    <LabelPrimaryLarge>{selectedPeriodPlan ? formatMoney(selectedPeriodPlan.unit_amount * 0.01) : 'R$0,00'}</LabelPrimaryLarge>
                  </Column>
                </Row>
              </AccordionContentPadded>
            )}
          </AccordionBoxFlatTop>

          <Row className="mt-4">
            <Column sm={6}>
              <Button variant="secondary" onClick={() => goToLoginPage()}>
                {t('company:onboard.choosePlan.accessNow')}
              </Button>
            </Column>
            <Column sm={6}>
              <Button variant="primary" onClick={() => mutation.mutate(selectedPeriodPlan)}>
                {t('company:onboard.choosePlan.contractNow')}
              </Button>
            </Column>
          </Row>
        </Column>
      </Row>
    </PageContainer>
  );
};
